import { Trans, useLingui } from '@lingui/react/macro'
import { ErrorAlert } from '$src/components/ErrorAlert'
import {
  maintenanceLogHooks,
  maintenanceTaskHooks,
  packageVersionTaskHooks,
  propertyHooks,
} from '$src/lib/trpcHooks'
import { Carousel } from '@mantine/carousel'
import type { EmblaCarouselType } from 'embla-carousel'
import '@mantine/carousel/styles.css'
import { Box, Loader, Stack, Text } from '@mantine/core'
import { PlaceholderContent } from '@markku/components'
import {
  MaintenanceLogWithRelations,
  MaintenanceTaskWithSteps,
} from '@markku/types'
import { useState } from 'react'
import { MarkkuSavingsCard } from '../components/MarkkuSavingsCard'
import {
  SystemTaskDetailsData,
  SystemTaskDetailsModal,
} from '../components/SystemTaskDetailsModal'
import { TaskDetailsModal } from '../components/TaskDetailsModal'
import { YearSummaryCard } from '../components/YearSummaryCard'
import classes from '../YearCalendarCarousel.module.css'
import { HistoryLinkCard } from './HistoryLinkCard'
import { SeasonCard } from './SeasonCard'
import { buildSeasonData } from './buildSeasonData'
import {
  getSeasonAndYearFromDeadline,
  MaintenanceTask,
  TargetSeason,
} from './utils'

type ModalState =
  | { type: 'closed' }
  | {
      type: 'task'
      task?: MaintenanceTaskWithSteps
      log?: MaintenanceLogWithRelations
      targetSeason?: TargetSeason
    }
  | { type: 'systemTask'; data: SystemTaskDetailsData }

interface YearCalendarCarouselProps {
  propertyId: number
}

export function YearCalendarCarousel({
  propertyId,
}: YearCalendarCarouselProps) {
  const { t } = useLingui()
  const [modal, setModal] = useState<ModalState>({ type: 'closed' })
  const [carouselApi, setCarouselApi] = useState<EmblaCarouselType | null>(null)

  const { data: property, isLoading: propertyLoading } =
    propertyHooks.useGetById(propertyId)

  const { data: tasksData, isLoading: tasksLoading } =
    maintenanceTaskHooks.useList({
      pageSize: 100,
      filters: {
        $or: [
          { propertyId: { eq: propertyId } },
          { propertyId: { isNull: true } },
        ],
      },
    })

  const { data: logsData, isLoading: logsLoading } =
    maintenanceLogHooks.useList({
      pageSize: 100,
      filters: { propertyId: { eq: propertyId } },
    })

  const activeSubscription = property?.subscriptions?.find(
    (sub) => sub.status === 'active',
  )
  const packageVersionId =
    activeSubscription?.subscriptionPackageVersions?.[0]?.servicePackageVersion
      ?.id

  const { data: packageTasksData, isLoading: packageTasksLoading } =
    packageVersionTaskHooks.useList({
      pageSize: 100,
      filters: packageVersionId
        ? { servicePackageVersionId: { eq: packageVersionId } }
        : { servicePackageVersionId: { eq: -1 } },
    })

  const isLoading =
    tasksLoading || logsLoading || packageTasksLoading || propertyLoading

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={300}>
        <Loader size="lg" />
        <Text size="sm" c="dimmed">
          <Trans>Loading maintenance tasks...</Trans>
        </Text>
      </Stack>
    )
  }

  if (!tasksData || !logsData) {
    return <ErrorAlert />
  }

  const tasks = tasksData.data || []
  const logs = logsData.data || []
  const packageTasks = packageTasksData?.data || []
  const hasPackage = !!packageVersionId

  const { seasons, logsByTaskAndSeasonYear, currentSeasonSlideIndex } =
    buildSeasonData({
      tasks,
      logs,
      packageTasks,
      propertyId,
      hasPackage,
    })

  const handleTaskClick = (
    task: MaintenanceTask,
    seasonKey: string,
    year: number,
  ) => {
    const taskId = parseInt(task.id)
    const fullTask = tasks.find((t) => t.id === taskId)
    // Look up log for this specific task + season + year
    const logKey = `${taskId}-${seasonKey}-${year}`
    const log = logsByTaskAndSeasonYear.get(logKey)

    if (!task.isSystemTask) {
      setModal({ type: 'task', task: fullTask, log })
    } else {
      setModal({
        type: 'systemTask',
        data: {
          id: task.id,
          title: task.title,
          description: task.description,
          category: task.category,
          estimatedDuration: task.estimatedDurationHours
            ? t`About ${task.estimatedDurationHours} hours`
            : undefined,
          isUserTask: task.isUserTask,
          completed: task.completed,
          attachments: task.attachments,
          log: log || undefined,
        },
      })
    }
  }

  const handleAddTask = (targetSeason?: TargetSeason) => {
    setModal({ type: 'task', targetSeason })
  }

  const handleModalSave = (taskDeadline?: string) => {
    if (taskDeadline && carouselApi) {
      const { season, year } = getSeasonAndYearFromDeadline(taskDeadline)
      const seasonIndex = seasons.findIndex(
        (s) => s.season === season && s.year === year,
      )
      if (seasonIndex !== -1) {
        window.setTimeout(() => {
          carouselApi.scrollTo(seasonIndex + 1)
        }, 300)
      }
    }
  }

  const closeModal = () => setModal({ type: 'closed' })

  return (
    <Stack gap="md">
      <PlaceholderContent>
        <YearSummaryCard />
      </PlaceholderContent>

      <Carousel
        withIndicators={false}
        withControls={false}
        height="auto"
        slideSize="100%"
        slideGap={{ base: 'md', sm: 'lg' }}
        emblaOptions={{
          skipSnaps: false,
          containScroll: 'trimSnaps',
          align: 'start',
          startIndex: currentSeasonSlideIndex + 1,
        }}
        getEmblaApi={setCarouselApi}
        classNames={{ container: classes.carouselPeek }}
      >
        <Carousel.Slide key="history-start">
          <HistoryLinkCard propertyId={propertyId} />
        </Carousel.Slide>

        {seasons.map((season, index) => (
          <Carousel.Slide key={`${season.season}-${index}`}>
            <SeasonCard
              season={season}
              onTaskClick={handleTaskClick}
              onAddTask={() =>
                handleAddTask({ season: season.season, year: season.year })
              }
              onPrevious={() => carouselApi?.scrollPrev()}
              onNext={() => carouselApi?.scrollNext()}
              hasPackage={hasPackage}
            />
          </Carousel.Slide>
        ))}

        <Carousel.Slide key="history-end">
          <HistoryLinkCard propertyId={propertyId} />
        </Carousel.Slide>
      </Carousel>

      <PlaceholderContent>
        <Box mt="xl" pb="md">
          <MarkkuSavingsCard propertyId={propertyId} />
        </Box>
      </PlaceholderContent>

      {modal.type === 'task' && (
        <TaskDetailsModal
          task={modal.task}
          log={modal.log}
          propertyId={propertyId}
          opened
          onClose={closeModal}
          targetSeason={modal.targetSeason}
          onSave={handleModalSave}
        />
      )}

      {modal.type === 'systemTask' && (
        <SystemTaskDetailsModal
          onClose={closeModal}
          task={modal.data}
          propertyId={propertyId}
          hasPackage={hasPackage}
          onSave={handleModalSave}
        />
      )}
    </Stack>
  )
}
